import React, { useEffect, useState } from "react";
import { useIntegration } from "../hooks/useIntegration";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Backdrop from "@mui/material/Backdrop";
import Alert from "@mui/material/Alert";
import SyncIcon from "@mui/icons-material/Sync";
import { integrationApiInstance } from "../apis/integrationApi";
import { getApiErrorMessage } from "../utils/apiErrorMessage";
import { useUserMsp } from "../hooks/useUserMsp";
import { isMspOrgOrHiddenCustomer } from "../utils/mspOrgCustomer";
import { onboardingApi } from "../apis/onboardingApi";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { getDropdownSafeDialogSx } from "../utils/dialogLayering";
import LayeredAutocompleteSelect from "../components/LayeredAutocompleteSelect";

const Customers: React.FC = () => {
  const {
    customers,
    customersLoading,
    customersLoaded,
    customersError,
    getCustomersByMspId,
    createCustomer,
    updateCustomer,
    deleteCustomer,
  } = useIntegration();
  const {
    userMspInfo,
    isLoading: userMspLoading,
    error: userMspError,
  } = useUserMsp();

  // State for dialogs and forms
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [formData, setFormData] = useState({
    name: '',
    psa_customer_id: ''
  });
  // PSA customers dropdown state
  const [psaCustomers, setPsaCustomers] = useState<Array<{ id: string; name: string }>>([]);
  const [loadingPsaCustomers, setLoadingPsaCustomers] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncNotice, setSyncNotice] = useState<{ severity: 'success' | 'error'; message: string } | null>(null);

  const handleSync = async () => {
    if (!userMspInfo?.msp_id || syncing) return;
    setSyncing(true);
    setSyncNotice(null);
    try {
      const result = await integrationApiInstance.syncPsaCustomers();
      getCustomersByMspId(String(userMspInfo.msp_id));
      setPsaCustomers([]);
      setSyncNotice({ severity: 'success', message: `Customers synced: ${result.imported} added, ${result.updated} updated, ${result.skipped} unchanged.` });
    } catch (error) {
      setSyncNotice({ severity: 'error', message: getApiErrorMessage(error, 'Failed to sync customers. Please try again.') });
    } finally {
      setSyncing(false);
    }
  };
  const sortedCustomers = [...customers]
    .filter((c: any) => !isMspOrgOrHiddenCustomer(c))
    .sort((a: any, b: any) =>
    String(a?.name || "").localeCompare(String(b?.name || ""), undefined, {
      sensitivity: "base",
    })
  );

  useEffect(() => {
    if (userMspInfo?.msp_id) {
      getCustomersByMspId(String(userMspInfo.msp_id));
    }
  }, [getCustomersByMspId, userMspInfo?.msp_id]);

  // Fetch PSA customers when create dialog opens
  useEffect(() => {
    if (isCreateDialogOpen && userMspInfo?.msp_id && psaCustomers.length === 0 && !loadingPsaCustomers) {
      const fetchPsaCustomers = async () => {
        setLoadingPsaCustomers(true);
        try {
          const customers = await onboardingApi.getPsaCustomers(userMspInfo.msp_id);
          // Filter and map to ensure we have valid id and name fields
          const validCustomers = (customers || [])
            .filter((c) => c.id && c.name)
            .map((c) => ({
              id: String(c.id!),
              name: String(c.name!),
            }))
            .sort((a, b) =>
              a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
            );
          setPsaCustomers(validCustomers);
        } catch (error) {
          console.error("Failed to fetch PSA customers:", error);
          // Don't show error to user, just allow manual entry
        } finally {
          setLoadingPsaCustomers(false);
        }
      };
      fetchPsaCustomers();
    }
  }, [isCreateDialogOpen, userMspInfo?.msp_id, psaCustomers.length, loadingPsaCustomers]);

  // Handle PSA customer selection from dropdown
  const handlePsaCustomerSelect = (selectedId: string) => {
    // Pre-fill customer name from selected PSA customer, but keep it editable
    const selectedCustomer = psaCustomers.find((c) => c.id === selectedId);
    if (selectedCustomer) {
      // Always pre-fill customer name when a customer is selected, but user can still edit it
      setFormData((prev) => ({ ...prev, name: selectedCustomer.name, psa_customer_id: selectedId }));
    } else {
      // Just set the PSA customer ID if customer not found (shouldn't happen, but safety check)
      setFormData((prev) => ({ ...prev, psa_customer_id: selectedId }));
    }
  };

  // Handle create customer
  const handleCreateCustomer = async () => {
    try {
      await createCustomer({
        ...formData,
        msp_id: userMspInfo?.msp_id
      });
      setIsCreateDialogOpen(false);
      setFormData({ name: '', psa_customer_id: '' });
      // Reset PSA customers list so it refetches next time
      setPsaCustomers([]);
      // The Redux state will be updated automatically by the action
    } catch (error) {
      console.error('Error creating customer:', error);
    }
  };

  // Handle edit customer
  const handleEditCustomer = async () => {
    try {
      await updateCustomer(selectedCustomer.id, formData);
      setIsEditDialogOpen(false);
      setSelectedCustomer(null);
      setFormData({ name: '', psa_customer_id: '' });
      // The Redux state will be updated automatically by the action
    } catch (error) {
      console.error('Error updating customer:', error);
    }
  };

  // Handle delete customer
  const handleDeleteCustomer = async () => {
    try {
      await deleteCustomer(selectedCustomer.id);
      setIsDeleteDialogOpen(false);
      setSelectedCustomer(null);
      // The Redux state will be updated automatically by the action
    } catch (error) {
      console.error('Error deleting customer:', error);
    }
  };

  // Open edit dialog
  const openEditDialog = (customer: any) => {
    setSelectedCustomer(customer);
    setFormData({
      name: customer.name || '',
      psa_customer_id: customer.psa_customer_id || ''
    });
    setIsEditDialogOpen(true);
  };

  // Open delete dialog
  const openDeleteDialog = (customer: any) => {
    setSelectedCustomer(customer);
    setIsDeleteDialogOpen(true);
  };

  // Reset form data
  const resetFormData = () => {
    setFormData({ name: '', psa_customer_id: '' });
    setSelectedCustomer(null);
    // Reset PSA customers list so it refetches next time dialog opens
    setPsaCustomers([]);
  };

  return (
    <div style={{ margin: "2rem auto" }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2 }}>
        <Button
          variant="outlined"
          startIcon={syncing ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
          onClick={handleSync}
          disabled={syncing || userMspLoading || !userMspInfo?.msp_id}
          sx={{ fontFamily: 'var(--theo-font-sans)' }}
        >
          {syncing ? 'Syncing…' : 'Sync'}
        </Button>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setIsCreateDialogOpen(true)}
          disabled={syncing || userMspLoading || !userMspInfo?.msp_id}
          sx={{
            backgroundColor: '#705d4a',
            '&:hover': {
              backgroundColor: '#5a4a3a',
            },
            fontFamily: 'var(--theo-font-sans)',
            fontWeight: 600,
          }}
        >
          Add Customer
        </Button>
      </Box>

      {syncNotice && <Alert severity={syncNotice.severity} sx={{ mb: 2 }} onClose={() => setSyncNotice(null)}>{syncNotice.message}</Alert>}

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: 2,
          boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
          border: "1px solid #E0E0E0",
          maxHeight: 'calc(100vh - 180px)',
          overflowY: "auto",
        }}
      >
        <Table stickyHeader sx={{ minWidth: 700, fontFamily: 'var(--theo-font-sans)' }}>
          <TableHead>
            <TableRow sx={{ background: "#F7F7F7" }}>
              <TableCell sx={{ textAlign: "center", fontFamily: "var(--theo-font-sans)", fontSize: "12px", fontWeight: 500, color: "#838383", cursor: "pointer" }}>Name</TableCell>
              <TableCell sx={{ textAlign: "center", fontFamily: "var(--theo-font-sans)", fontSize: "12px", fontWeight: 500, color: "#838383", cursor: "pointer" }}>PSA Account ID</TableCell>
              <TableCell sx={{ textAlign: "center", fontFamily: "var(--theo-font-sans)", fontSize: "12px", fontWeight: 500, color: "#838383", cursor: "pointer" }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(userMspLoading ||
              customersLoading ||
              (!customersError && !customersLoaded && userMspInfo?.msp_id)) &&
            customers.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={3}
                  sx={{
                    textAlign: 'center',
                    fontFamily: 'var(--theo-font-sans)',
                    color: '#6B7280',
                    py: 6,
                    fontSize: 16,
                    fontWeight: 500,
                    background: "#FAFAFA",
                    borderBottom: "none"
                  }}
                >
                  <CircularProgress size={20} sx={{ color: "#705d4a", mb: 1 }} />
                  <br />
                  Loading customers...
                </TableCell>
              </TableRow>
            ) : (userMspError || customersError) && customers.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={3}
                  sx={{
                    textAlign: 'center',
                    fontFamily: 'var(--theo-font-sans)',
                    color: '#d32f2f',
                    py: 6,
                    fontSize: 16,
                    fontWeight: 500,
                    background: "#FAFAFA",
                    borderBottom: "none"
                  }}
                >
                  {userMspError || customersError}
                </TableCell>
              </TableRow>
            ) : customers.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={3}
                  sx={{
                    textAlign: 'center',
                    fontFamily: 'var(--theo-font-sans)',
                    color: '#6B7280',
                    py: 6,
                    fontSize: 16,
                    fontWeight: 500,
                    background: "#FAFAFA",
                    borderBottom: "none"
                  }}
                >
                  <InfoOutlinedIcon sx={{ fontSize: 28, color: "#B0B4BA", mb: 1 }} />
                  <br />
                  No customers found
                </TableCell>
              </TableRow>
            ) : (
              sortedCustomers.map((customer: any, idx: number) => {
                return (
                  <TableRow
                    key={customer.id}
                    sx={{
                      background: idx % 2 === 0 ? "#FAFAFA" : "#FFF",
                      "&:hover": { background: "#F0F0F0" },
                      transition: "background 0.2s"
                    }}
                  >
                    <TableCell sx={{ py: 1.5, fontSize: 14, fontFamily: 'var(--theo-font-sans)', textAlign: 'center', cursor: 'pointer' }}>{customer.name}</TableCell>
                    <TableCell sx={{ py: 1.5, fontSize: 14, fontFamily: 'var(--theo-font-sans)', textAlign: 'center', cursor: 'pointer' }}>{customer.psa_customer_id || '-'}</TableCell>
                    <TableCell sx={{ py: 1.5, textAlign: 'center' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => openEditDialog(customer)}
                          sx={{
                            color: '#705d4a',
                            '&:hover': {
                              backgroundColor: 'rgba(112, 93, 74, 0.1)',
                            },
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => openDeleteDialog(customer)}
                          sx={{
                            color: '#705d4a',
                            '&:hover': {
                              backgroundColor: 'rgba(112, 93, 74, 0.1)',
                            },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create Customer Dialog */}
      <Dialog 
        open={isCreateDialogOpen} 
        onClose={() => {
          setIsCreateDialogOpen(false);
          resetFormData();
        }} 
        maxWidth="sm" 
        fullWidth
        sx={getDropdownSafeDialogSx()}
      >
        <DialogTitle sx={{ fontFamily: 'var(--theo-font-sans)', fontWeight: 600 }}>Add New Customer</DialogTitle>
        <DialogContent sx={{ overflow: "visible" }}>
          <Box sx={{ pt: 2, position: 'relative' }}>
            <Backdrop
              open={loadingPsaCustomers}
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 1,
                color: '#705d4a',
                backgroundColor: 'rgba(255,255,255,0.55)',
                borderRadius: 1,
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={24} sx={{ color: '#705d4a' }} />
                <Typography sx={{ fontFamily: 'var(--theo-font-sans)', fontSize: 13, color: '#4B5563' }}>
                  Loading platform customers...
                </Typography>
              </Box>
            </Backdrop>
            {/* PSA Customer Dropdown - Select from platform customers */}
            <LayeredAutocompleteSelect
              label="Platform Customer"
              value={formData.psa_customer_id}
              onChange={handlePsaCustomerSelect}
              disabled={loadingPsaCustomers}
              loading={loadingPsaCustomers}
              required
              noOptionsText={loadingPsaCustomers ? "Loading customers..." : "No customers available"}
              options={psaCustomers.map((customer) => ({
                value: customer.id,
                label: customer.name,
              }))}
              listboxMaxHeight={520}
              textFieldSx={{ mb: 2 }}
            />
            
            {/* Customer Name - Editable, pre-filled from dropdown selection */}
            <TextField
              fullWidth
              label="Customer Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              sx={{ mb: 2 }}
              required
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={() => {
              setIsCreateDialogOpen(false);
              resetFormData();
            }} 
            sx={{ fontFamily: 'var(--theo-font-sans)' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateCustomer}
            variant="contained"
            disabled={!formData.name.trim() || !formData.psa_customer_id.trim()}
            sx={{
              backgroundColor: '#705d4a',
              '&:hover': { backgroundColor: '#5a4a3a' },
              fontFamily: 'var(--theo-font-sans)',
            }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Customer Dialog */}
      <Dialog open={isEditDialogOpen} onClose={() => setIsEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: 'var(--theo-font-sans)', fontWeight: 600 }}>Edit Customer</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Customer Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              sx={{ mb: 2 }}
              required
            />
            <TextField
              fullWidth
              label="PSA Account ID"
              value={formData.psa_customer_id}
              onChange={(e) => setFormData({ ...formData, psa_customer_id: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setIsEditDialogOpen(false)} sx={{ fontFamily: 'var(--theo-font-sans)' }}>
            Cancel
          </Button>
          <Button
            onClick={handleEditCustomer}
            variant="contained"
            disabled={!formData.name.trim()}
            sx={{
              backgroundColor: '#705d4a',
              '&:hover': { backgroundColor: '#5a4a3a' },
              fontFamily: 'var(--theo-font-sans)',
            }}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onClose={() => setIsDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: 'var(--theo-font-sans)', fontWeight: 600 }}>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: 'var(--theo-font-sans)', fontSize: 16 }}>
            Are you sure you want to delete the customer "{selectedCustomer?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setIsDeleteDialogOpen(false)} sx={{ fontFamily: 'var(--theo-font-sans)' }}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteCustomer}
            variant="contained"
            sx={{
              backgroundColor: '#d32f2f',
              '&:hover': { backgroundColor: '#b71c1c' },
              fontFamily: 'var(--theo-font-sans)',
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Customers;
